import { DropdownPanel } from "@smartsuite/react-ui/lib/components/dropdowns/DropdownPanel/DropdownPanel";
import { Tooltip } from "@smartsuite/react-ui/lib/components/help/Tooltip/Tooltip";
import { Icon } from "@smartsuite/react-ui/lib/components/utils/Icon/Icon";
import { Colors } from "@smartsuite/types";
import cx from "classnames";
import {
  type FunctionComponent,
  type LegacyRef,
  memo,
  useCallback,
  useRef,
  useState,
} from "react";
import type { ColumnAlignment } from "../../../canvas/state/types";
import { texts } from "./TableColumnAlignment.text";

const alignmentList: ColumnAlignment[] = ["left", "center", "right"];

interface TableColumnAlignmentProps {
  value?: ColumnAlignment;
  onChange: (alignment: ColumnAlignment) => void;
}

const TableColumnAlignmentRaw: FunctionComponent<TableColumnAlignmentProps> = ({
  value = "left",
  onChange,
}) => {
  const [isActive, setIsActive] = useState(false);
  const targetElementRef = useRef<HTMLButtonElement>();

  const handleClickOutside = useCallback(() => {
    setIsActive(false);
  }, []);

  const classes = cx("table-column-alignment", {
    "is-active": isActive,
  });

  const handleToggle = useCallback(() => setIsActive((prev) => !prev), []);

  const handleChangeAlignment = useCallback(
    (alignment: ColumnAlignment) => {
      if (value === alignment) {
        return;
      }
      onChange(alignment);
      setIsActive(false);
    },
    [onChange, value],
  );

  return (
    <>
      <Tooltip
        className="table-column-alignment-tooltip"
        placements={["top", "bottom"]}
        text={texts[value]}
        padding={2}
        target={
          <button
            type="button"
            className={classes}
            ref={targetElementRef as LegacyRef<HTMLButtonElement>}
            onClick={handleToggle}
          >
            <Icon
              name={`formatting-align-${value}`}
              className="table-column-alignment__icon"
              size="S"
              color="currentColor"
              transition={true}
            />
          </button>
        }
      ></Tooltip>
      <DropdownPanel
        my="left top"
        at="left bottom+2"
        targetElementRef={targetElementRef}
        isActive={isActive}
        onClickOutside={handleClickOutside}
      >
        <div className="table-column-alignment-content">
          {alignmentList.map((alignment: ColumnAlignment) => (
            <AlignmentButton
              key={alignment}
              value={alignment}
              isSelected={value === alignment}
              onClick={handleChangeAlignment}
            />
          ))}
        </div>
      </DropdownPanel>
    </>
  );
};

export const TableColumnAlignment = memo(TableColumnAlignmentRaw);

type AlignmentButtonProps = {
  value: ColumnAlignment;
  isSelected: boolean;
  onClick: (alignment: ColumnAlignment) => void;
};

const AlignmentButtonRaw: FunctionComponent<AlignmentButtonProps> = ({
  value,
  isSelected,
  onClick,
}) => {
  const handleClick = useCallback(() => onClick(value), [onClick, value]);

  return (
    <button
      type="button"
      key={value}
      className="table-column-alignment-content__item"
      onClick={handleClick}
    >
      <Icon
        name={`formatting-align-${value}`}
        size="S"
        className="table-column-alignment-content__icon"
        color="currentColor"
      />

      {texts[value]}

      {isSelected && (
        <Icon
          className="table-column-alignment-content__checkmark"
          name="checkmark"
          size="S"
          color={Colors.bGreen}
        />
      )}
    </button>
  );
};
const AlignmentButton = memo(AlignmentButtonRaw);
